import { useTrackingTrigger } from 'components/templates/ThirdPartyTrackingProvider/ThirdPartyTrackingProvider';
import { useRouter } from 'next/router';
import { ReactElement, useEffect } from 'react';
import { useSurveyCardFragmentQuery_surveyRef$key } from './__generated__/useSurveyCardFragmentQuery_surveyRef.graphql';
import SurveyCardStatic from './SurveyCardStatic';
import { useSurveyCardFragmentQuery } from './useSurveyCardFragmentQuery';

export type Props = {
  className?: string;
  surveyRef: useSurveyCardFragmentQuery_surveyRef$key;
  disabled?: boolean;
  isOnGoingSurvey?: boolean;
};

function SurveyCard({ className, surveyRef, disabled, isOnGoingSurvey }: Props): ReactElement {
  const survey = useSurveyCardFragmentQuery(surveyRef);
  const trackingTrigger = useTrackingTrigger();
  const router = useRouter();

  /**
   * If the appropriate query parameters are present, automatically open the survey.
   */
  useEffect(() => {
    if (
      router.query.direct_to_survey &&
      router.query.survey_id &&
      router.query.direct_to_survey === 'true' &&
      router.query.survey_id === survey.studyId &&
      survey.url
    ) {
      const params = new URLSearchParams(router.query as Record<string, string>);
      params.delete('direct_to_survey');
      params.delete('survey_id');
      router
        .replace({ pathname: router.pathname, query: params.toString() }, undefined, {
          shallow: true,
        })
        .then(() => {
          window.location.href = survey.url as string;
        });
    }
  }, [survey.url, survey.id, router, survey.studyId]);

  const baseAmount = survey.baseReward != null ? survey.baseReward.cents / 100 : undefined;
  const hasBoost = Boolean(
    survey.baseReward && survey.baseReward.formatted !== survey.rewardAmount?.formatted,
  );

  return (
    <SurveyCardStatic
      surveyId={
        survey.tags.includes('screener') ? '' : survey.supportId || survey.id.substring(0, 8)
      }
      title={survey.title}
      surveyUrl={survey.url ? (survey.url as string) : undefined}
      amount={(survey.rewardAmount?.subUnitAmount || 0) / 100}
      baseAmount={hasBoost ? baseAmount : undefined}
      duration={survey.durationDetails.providerDuration}
      tags={survey.tags}
      currency={survey.rewardAmount?.currency || 'USD'}
      onSurveyClick={trackingTrigger}
      className={className}
      disabled={disabled}
      isOnGoingSurvey={isOnGoingSurvey}
    />
  );
}

export default SurveyCard;
